/**
 * <CameraPreviewFrame> — le cadre de l'aperçu de la caméra, et ce qu'on y lit (molecule).
 *
 * Au test du matériel, l'élève juge lui-même son image : se voir vaut mieux que se l'entendre dire.
 * L'image vient de l'écran (`children` : le flux de la caméra, un effet) ; le cadre porte ce qui
 * l'accompagne :
 *  - « Couper ma vidéo », posé en bas de l'image : un élève qui ne veut pas être filmé doit
 *    pouvoir le dire avant d'entrer. Jamais sur un cadre vide ;
 *  - la vidéo coupée : le cadre dit « Appuie pour activer ta vidéo », et tout le cadre presse ;
 *  - l'image noire : une chip posée sur l'image, pas un bandeau qui la barre ;
 *  - une caméra qui ne s'ouvre pas : la raison, en haut, sur l'image d'avant.
 *
 * Paysage dans le parcours (sous le conseil de l'étape caméra), portrait sur la carte « déjà
 * vérifiés » d'un téléphone : un grand cadre de soi, comme les applis de visio.
 *
 * Composes DS pieces only — `<Icon>`, `<Text>`, `<Badge>`, `<Banner>`.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Banner } from "../../atoms/Banner"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"

export type CameraPreviewFrameProps = {
    /** L'image de la caméra — le flux, que l'écran monte. */
    children?: React.ReactNode
    /** `landscape` (16:9, défaut) ou `portrait` (3:4). */
    orientation?: "landscape" | "portrait"
    /** La vidéo est coupée : le cadre propose de la rallumer (`onEnableVideo`). */
    videoOff?: boolean
    onEnableVideo?: () => void
    /** L'image est toute noire : quelque chose cache l'objectif. */
    obstructed?: boolean
    /** La caméra demandée ne s'ouvre pas : pourquoi. L'image affichée est encore la précédente. */
    deviceError?: string
    /** « Couper ma vidéo ». Absent : pas de bouton. */
    onMuteVideo?: () => void
    testID?: string
}

const TEXTS = {
    enable: "Appuie pour activer ta vidéo",
    obstructed: "Image toute noire",
    mute: "Couper ma vidéo",
}

const RATIO = { landscape: 16 / 9, portrait: 3 / 4 }

export function CameraPreviewFrame(props: CameraPreviewFrameProps) {
    const {
        children,
        orientation = "landscape",
        videoOff = false,
        onEnableVideo,
        obstructed = false,
        deviceError,
        onMuteVideo,
        testID,
    } = props

    if (videoOff) {
        return (
            <Pressable
                onPress={onEnableVideo}
                disabled={onEnableVideo == null}
                accessibilityRole="button"
                style={[styles.frame(RATIO[orientation]), styles.center]}
                testID={testID}
            >
                <Icon name="video-slash" size={24} color="text-on-night" />
                <Text size="text-sm" color="text-on-night">
                    {TEXTS.enable}
                </Text>
            </Pressable>
        )
    }

    return (
        <View style={styles.frame(RATIO[orientation])} testID={testID}>
            <View style={styles.image}>{children}</View>

            {deviceError != null && deviceError !== "" && (
                <View style={styles.top}>
                    <Banner message={deviceError} icon="exclamation-triangle" color="warning" tinted align="start" />
                </View>
            )}
            {obstructed && deviceError == null && (
                <View style={styles.topLeft}>
                    <Badge text={TEXTS.obstructed} variant="danger" iconName="eye-slash" />
                </View>
            )}
            {onMuteVideo != null && children != null && (
                <Pressable onPress={onMuteVideo} accessibilityRole="button" style={styles.mute}>
                    <Icon name="video" size={16} color="white" />
                    <Text size="text-sm" weight="semibold" color="white">
                        {TEXTS.mute}
                    </Text>
                </Pressable>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    frame: (ratio: number) => ({
        width: "100%" as const,
        aspectRatio: ratio,
        borderRadius: theme.radius.lg,
        overflow: "hidden" as const,
        backgroundColor: theme.colors["surface-night"],
    }),
    center: {
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing["2"],
    },
    image: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    top: {
        position: "absolute",
        top: theme.spacing["3"],
        left: theme.spacing["3"],
        right: theme.spacing["3"],
    },
    topLeft: {
        position: "absolute",
        top: theme.spacing["3"],
        left: theme.spacing["3"],
    },
    /** Posé sur l'image, au milieu en bas : un voile, pas un bouton de plus sur la carte. */
    mute: {
        position: "absolute",
        bottom: theme.spacing["3"],
        alignSelf: "center",
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["2"],
        minHeight: 36,
        paddingHorizontal: theme.spacing["4"],
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.backdrop,
    },
}))
